import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  addPremiseToBillCycle,
  archiveBillCycle,
  getBillCycleDetails,
  getBillCycleLastRunDetails,
  getBillCycleListForMovePremise,
  getChoices,
  getOutstandingBills,
  getPremisesForBillCycle,
  getTerritoryConfigMap,
  moveBillCycle,
  updateBillCycle,
} from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";
import { useNavigate } from "react-router-dom";

export const useChoices = (choice: string) =>
  useSmartQuery(
    QueryKeyFactory.module.bx.choices(choice),
    () => getChoices(choice),
    {
      enabled: !!choice,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 0,
    }
  );

export const usePremisesForBillCycle = (queryParams: {
  category?: string[];
  subCategory?: string[];
  area?: string;
  subArea?: string;
  zone?: string;
  division?: string;
  limit?: number;
  page?: number;
  search?: string;
  remoteUtilityId?: string;
  premises?: string[];
  excludePremises?: boolean;
}) => {
  const hasValues = (arr?: unknown[]) => Array.isArray(arr) && arr.length > 0;

  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.premisesForBillCycle(), queryParams],
    () => getPremisesForBillCycle(queryParams),
    {
      enabled:
        hasValues(queryParams.category) && hasValues(queryParams.subCategory),
    }
  );
};

export const useAreaList = (divisionCodes?: string) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.areaList(), divisionCodes],
    () => getTerritoryConfigMap("area", divisionCodes)
  );
};
export const useSubAreaList = (areaList: string) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.subAreaList(), areaList],
    () => getTerritoryConfigMap("sub_area", areaList),
    // {
    //   enabled: !!areaList, // <--- ADD THIS
    // }
  );
};

export const useZoneList = () => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.zoneList()],
    () => getTerritoryConfigMap("zone"),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useDivisionList = (zoneCodes: string) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.billCycle.divisionList(), zoneCodes],
    () => getTerritoryConfigMap("division", zoneCodes),
    // {
    //   enabled: !!zoneCodes,
    // }
  );
};

export const useBillCycleDetails = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleDetails(), billCycleId],
    () => getBillCycleDetails(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useUpdateBillCycle = (billCycleId: string) => {
  const navigate = useNavigate();
  return useSmartMutation<any, Error, any>(
    (payload: FormData) => updateBillCycle(payload, billCycleId),
    {
      onSuccess: () => {
        navigate("/bx/bill-setup/billcycle");
        const key = QueryKeyFactory.module.bx.billCycle.billCycleList();

        queryClient.invalidateQueries({
          queryKey: key,
          exact: false,
        });

        queryClient.refetchQueries({
          queryKey: key,
          exact: false,
        });
      },
      onError: (error: Error) => {
        // toast.error(error.message);
      },
    }
  );
};

export const useArchiveBillCycle = (billCycleId: string) =>
  useSmartMutation(() => archiveBillCycle(billCycleId), {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.bx.billCycle.billCycleList(),
        exact: false,
      });
    },
  });

export const useBillCycleLastRunDetails = (billCycleId: string | number) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleLastRunDetails(billCycleId)],
    () => getBillCycleLastRunDetails(billCycleId),
    {
      enabled: !!billCycleId,
    }
  );
};

export const useBillCycleListForMovePremise = (
  queryParams: Record<string, any>
) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billCycle.billCycleList(), queryParams],
    () => getBillCycleListForMovePremise(queryParams)
  );
};

export const useMoveBillCycle = () => {
  return useSmartMutation<any, Error, any>(
    (payload: FormData) => moveBillCycle(payload),
    {
      onSuccess: () => {
        const key = QueryKeyFactory.module.bx.billCycle.billCycleDetails();

        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.refetchQueries({
          queryKey: key,
        });
      },
      onError: (error: Error) => {
        // toast.error(error.message);
      },
    }
  );
};

export const useConsumerList = (
  queryParams: Record<string, any>,
  billCycleId?: string | number,
  isEnabled?: boolean
) => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billCycle.consumerList(),
      queryParams,
      billCycleId,
    ],
    () => getOutstandingBills(queryParams, billCycleId),
    {
      enabled: isEnabled,
    }
  );
};

export const useAddPremiseToBillCycle = (billCycleId: string) => {
  return useSmartMutation<any, Error, any>(
    (payload: Record<string, any>) =>
      addPremiseToBillCycle(billCycleId, payload),
    {
      onSuccess: () => {
        const key = [
          QueryKeyFactory.module.bx.billCycle.billCycleList(),
          QueryKeyFactory.module.bx.billCycle.billCycleDetails(),
          QueryKeyFactory.module.bx.billCycle.premisesForBillCycle(),
        ];

        queryClient.invalidateQueries({
          queryKey: key,
          exact: false,
        });

        queryClient.refetchQueries({
          queryKey: key,
          exact: false,
        });
        queryClient.refetchQueries({
          queryKey: ["bx", "billing", "details", "bill-cycle-details"],
          exact: false,
        });
        queryClient.refetchQueries({
          queryKey: ["bx", "billing", "list", "premisesTable"],
          exact: false,
        });
      },
    }
  );
};
